import AppInfoFooter from "@/components/AppInfoFooter";
import Button from "@/components/Button";
import HalfSpace from "@/components/HalfSpace";
import { PasswordInput } from "@/components/InputFields";
import Link from "@/components/Link";
import Page from "@/components/Page";
import { useCheckResetPasswordTokenQuery, useResetPasswordMutation } from "@/data/queries";
import { backendErrorCodeFor } from "@/model/BackendErrors";
import { passwordRegex } from "@/model/Constants";
import { VerifyCodePageStateParams } from "@/screens/auth/verify-code/VerifyCodePage";
import cn from "@/util/cn";
import { useSearchParams } from "@/util/hooks/useSearchParams";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";

type ResetPasswordPageSearchParams = {
  token: string;
  entityId: string;
};

type ResetPasswordFormData = {
  password: string;
  passwordConfirmation: string;
};

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams<ResetPasswordPageSearchParams>();
  const { token, entityId: userId } = searchParams;

  const navigate = useNavigate();
  const {
    register,
    watch,
    trigger: validate,
    handleSubmit,
    formState,
  } = useForm<ResetPasswordFormData>({ mode: "onBlur" });

  const { isPending: isValidatingResetPasswordToken, error: resetPasswordTokenValidationError } =
    useCheckResetPasswordTokenQuery({ token, userId });

  const {
    mutateAsync: resetPassword,
    isPending: isResettingPassword,
    error: resetPasswordError,
  } = useResetPasswordMutation();

  const { errors, touchedFields } = formState;
  const password = watch("password");
  const hasValidResetPasswordToken =
    !isValidatingResetPasswordToken && resetPasswordTokenValidationError === null;
  const resetPasswordErrorCode = backendErrorCodeFor(
    resetPasswordError ?? resetPasswordTokenValidationError
  );

  const validatePassword = (password: string) => passwordRegex.test(password);

  const validatePasswordConfirmation = (passwordConfirmation: string) =>
    passwordConfirmation === password || "Passwords do not match";

  const onSubmit = async (formData: ResetPasswordFormData) => {
    const result = await resetPassword({ token, userId, password: formData.password });
    if (result.type === "mfa-challenge") {
      const state: VerifyCodePageStateParams = { challenge: result.challenge };
      navigate("/verify-code", { state });
      return;
    }
    navigate("/", { replace: true });
  };

  return (
    <Page className="bg-gray-50">
      <div className="flex h-full flex-col justify-between">
        <div
          className={cn("mx-auto mt-10 w-full max-w-[480px] rounded-xl bg-white p-12 shadow-2xl")}
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            <h2 className="mb-4 text-center text-4xl text-primary/90">Reset password</h2>
            <h3 className="mb-8 text-center text-lg text-primary/90">
              Please create a new password below
            </h3>
            {resetPasswordErrorCode && (
              <div className="my-4 flex w-full items-center rounded-md border border-red-200 bg-red-100 p-4">
                <span className="material-icons me-4 text-red-600">error</span>
                <p className="text-red-600">
                  {resetPasswordErrorCode === "RESET_PASSWORD_TOKEN_EXPIRED" ? ( // pragma: allowlist secret
                    <>
                      This password reset link has expired.
                      <br />
                      <Link to="/forgot-password" replace={true} className="underline">
                        Resend password reset email
                      </Link>
                    </>
                  ) : resetPasswordErrorCode === "RESET_PASSWORD_ALREADY_EXECUTED" ? ( // pragma: allowlist secret
                    <>
                      The password was already reset via this link. Please&nbsp;
                      <Link to="/login" replace={true} className="underline">
                        log in
                      </Link>
                      .
                    </>
                  ) : resetPasswordErrorCode === "WEAK_PASSWORD" ? ( // pragma: allowlist secret
                    "The password is commonly used and easily guessed. Please choose a stronger password"
                  ) : resetPasswordErrorCode === "MFA_SETUP_REQUIRED" ? ( // pragma: allowlist secret
                    "Your account is not set up for two-step verification. Please contact Bloomlife Support."
                  ) : (
                    "Something went wrong. Please contact Bloomlife."
                  )}
                </p>
              </div>
            )}
            <label htmlFor="password" className="block leading-6 text-primary/90">
              Password
            </label>
            <PasswordInput
              id="password"
              autoComplete="new-password"
              className="mt-2 disabled:cursor-not-allowed"
              disabled={isResettingPassword || !hasValidResetPasswordToken}
              {...register("password", {
                validate: validatePassword,
                onChange: () => {
                  if (touchedFields.password) {
                    validate("password");
                  }
                },
              })}
            />
            <p
              className={cn(
                "mt-2 text-sm italic",
                errors?.password ? "text-red-600" : "text-primary"
              )}
            >
              Password must have at least 8&nbsp;characters that include at least 1&nbsp;lowercase
              character, 1&nbsp;uppercase character, 1&nbsp;number and one of these special
              characters:
              <span className="inline-block">
                $<HalfSpace />@<HalfSpace />%<HalfSpace />*<HalfSpace />#<HalfSpace />?
                <HalfSpace />!<HalfSpace />-<HalfSpace />_
              </span>
            </p>
            <label htmlFor="password-confirmation" className="mt-4 block leading-6 text-primary/90">
              Confirm password
            </label>
            <PasswordInput
              id="password-confirmation"
              autoComplete="new-password"
              className="mt-2 disabled:cursor-not-allowed"
              disabled={isResettingPassword || !hasValidResetPasswordToken}
              {...register("passwordConfirmation", {
                validate: validatePasswordConfirmation,
                onChange: () => {
                  if (touchedFields.passwordConfirmation) {
                    validate("passwordConfirmation");
                  }
                },
              })}
            />
            {errors?.passwordConfirmation && (
              <p className="mt-2 text-sm italic text-red-600">
                {errors.passwordConfirmation.message}
              </p>
            )}
            <Button
              variant="primary"
              type="submit"
              disabled={isResettingPassword || !hasValidResetPasswordToken}
              className="mt-8 w-full text-base"
            >
              Set password
            </Button>
          </form>
        </div>
        <AppInfoFooter className="mt-auto p-4" />
      </div>
    </Page>
  );
};

export default ResetPasswordPage;
